import polly from 'polly-js';

import { appConfig } from '../config/config.app';
import { captureException } from '../monitoring/logrocket';
import { actually } from '../utils/logging';

import { KNOWN_ERROR_CODES } from './constants';

export type HttPostArgs = {
  headers?: Headers;
  body: string;
};

export type ResponseInterceptor = (response: Response, retry: () => Promise<Response>) => Promise<Response> | Response;

let responseInterceptors: ResponseInterceptor[] = [];

export const addResponseInterceptor = (interceptor: ResponseInterceptor) => {
  responseInterceptors.push(interceptor);
};

export const clearResponseInterceptors = () => {
  responseInterceptors = [];
};

// Register default 401 retry interceptor
addResponseInterceptor(async (response, retry) => {
  if (response.status === 401) {
    const logMsg = 'Unauthorized (401) response received. Retrying request...';
    if (window.mgm?.logger?.warn) {
      window.mgm.logger.warn(logMsg, { url: response.url });
    } else {
      console.log(`[HTTP Interceptor] ${logMsg} URL: ${response.url}`);
    }

    // Automatically trigger token refresh before retry
    if (window.mgm?.authClient?.acquireUserTokens) {
      try {
        const refreshMsg = 'Attempting to refresh token after 401...';
        if (window.mgm?.logger?.info) {
          window.mgm.logger.info(refreshMsg);
        } else {
          console.log(`[HTTP Interceptor] ${refreshMsg}`);
        }
        await window.mgm.authClient.acquireUserTokens(true);
      } catch (err) {
        console.error('[HTTP Interceptor] Failed to refresh token during retry:', err);
        captureException(err instanceof Error ? err : new Error(String(err)), 'token-refresh-failure', {
          url: response.url,
        });
      }
    }

    return await retry();
  }
  return response;
});

const call = async (
  url: string,
  body?: string,
  headers?: Headers,
  requestOverrides?: RequestInit,
  sendCorrelationId = true,
  sendCredentials = true,
  externalEndpoint = false,
  isRetry = false,
): Promise<Response> => {
  const reqHeaders = new Headers({ 'Content-Type': 'application/json' });

  headers?.forEach((value, key) => {
    reqHeaders.set(key, value);
  });

  if (!externalEndpoint) {
    if (sendCorrelationId && window.mgm?.logger) {
      reqHeaders.append(window.mgm.logger.JOURNEY_ID_KEY, window.mgm.logger.getJourneyId());
      reqHeaders.append(window.mgm.logger.CORRELATION_ID_KEY, window.mgm.logger.getCorrelationId());
    }

    const token = await window.mgm?.authClient?.getAccessToken();
    if (token) {
      reqHeaders.append('Authorization', `Bearer ${token.accessToken}`);
    }
  }

  const requestOptions: RequestInit = {
    headers: reqHeaders,
    body: body,
    credentials: sendCredentials ? 'include' : undefined,
    ...requestOverrides,
  };

  let result: Response = {} as Response;

  const callEndpoint = async () => {
    result = await polly()
      .handle(error => {
        window.mgm?.logger?.debug('Polly Failure', {
          url,
          error,
        });
        return error instanceof TypeError;
      })
      .waitAndRetry([100, 200])
      .executeForPromise(() => fetch(url, requestOptions));
  };

  if (window.mgm?.logger) {
    await window.mgm.logger.captureTiming(callEndpoint, 'Request Timing', {
      url,
      journeyId: String(reqHeaders.get(window.mgm.logger.JOURNEY_ID_KEY)),
      correlationId: String(reqHeaders.get(window.mgm.logger.CORRELATION_ID_KEY)),
    });
  } else {
    await callEndpoint();
  }

  if (!isRetry) {
    for (const interceptor of responseInterceptors) {
      result = await interceptor(result, () =>
        call(url, body, headers, requestOverrides, sendCorrelationId, sendCredentials, externalEndpoint, true),
      );
    }
  }

  return result;
};

export const post = async (
  path: string,
  params: HttPostArgs,
  apiUrl = appConfig.apiUrl,
  sendCorrelationId = true,
  sendCredentials = true,
  externalEndpoint = false,
) => {
  try {
    const url = externalEndpoint ? path : `${apiUrl}${path}`;
    const result = await call(
      url,
      params.body,
      params.headers,
      { method: 'POST' },
      sendCorrelationId,
      sendCredentials,
      externalEndpoint,
    );

    if (!result.status) {
      throw Error('No response received');
    }

    if ([204].includes(result.status)) {
      return { response: {}, status: result.status };
    }

    const response = await result.json();

    if ([200, 201, 202, 203].includes(result.status)) {
      return { response, status: result.status };
    }

    if (!response.errorCode || (response.errorCode && !(response.errorCode in KNOWN_ERROR_CODES))) {
      throw Error(`Request failed with response status ${result.status}`);
    }

    return { response, status: result.status };
  } catch (error: unknown) {
    if (process.env.DEBUG) {
      actually.error('Failed Request', error);
    }
    if (error instanceof Error && error.message) {
      throw error;
    }

    throw Error(`Failed to process request, due to an unhandled error.`);
  }
};

export const get = (
  url: string,
  headers?: Headers,
  sendCorrelationId = true,
  sendCredentials = true,
): Promise<Response> => call(url, undefined, headers, { method: 'GET' }, sendCorrelationId, sendCredentials);
